import { useCallback, useContext, useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import Cookies from 'js-cookie';
import { MantineProvider } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { showNotification } from '@mantine/notifications';

import { lightTheme } from '~/config/theme';
import { logError } from '~/common/utils/log';
import useOpenIntercom from '~/components/Intercom/useOpenItercom';
import { UserContext } from '~/components/providers/UserProvider';
import { BRANDS } from '~/domains/auth/consts';
import SignInActions from '~/domains/auth/components/SignInActions';
import type { SignInFormValues } from '~/domains/auth/components/SignInActions/types';
import useRedirectAfterAuth from '~/domains/auth/hooks/useRedirectAfterAuth';
import { useEORedirect } from '~/domains/auth/hooks/useEORedirect';
import { useUserSchema } from '~/domains/auth/hooks/useUserSchema';
import { useGetBranding } from '~/domains/auth/hooks/useGetBranding';
import { SplashLoader } from '~/domains/common/components/SplashLoader/SplashLoader';
import useLogin from '~/domains/auth/hooks/useLogin';
import useEORedirectUrl from '~/domains/auth/hooks/useEORedirectUrl';
import { getSafeRedirectPath } from '~/domains/auth/utils/getSafeRedirectPath';
import { setTokens } from '~/domains/auth/utils/setTokens';
import { removeTokens } from '~/domains/auth/utils/removeTokens';
import { me } from '~/services/users/users.service.api';
import { reportEvent } from '~/domains/analytics';
import { Routes } from '~/domains/common/constants/routes';
import type { AuthSuccessResponse } from '~/services/identity/unauthenticated/identity.service.api';
import { getKycVerify } from '~/services/kyc/kyc.service.api';
import { SocureKycStatus } from '~/services/users/types';
import AuthModalsContextProvider from '~/domains/auth/context/AuthModalsContext/AuthModalsContextProvider';
import { AuthModalsContext } from '~/domains/auth/context/AuthModalsContext/AuthModalsContext';
import { RootModalsContext } from '~/domains/common/context/RootModalsContext/RootModalsContext';
import { mailToSupport } from '~/components/utils/mailToSupport';

import useStyles from './SignIn.styles';

function SignInContent() {
  const { t } = useTranslation('auth');
  const [isLoggingInitialised, setIsLoggingInitialised] = useState(false);
  const router = useRouter();
  const { classes } = useStyles();
  const { brand, platform, destination } = useGetBranding();
  const { signInSchema } = useUserSchema();
  const handleRedirectAfterAuth = useRedirectAfterAuth();
  const { setUser, setRedirect } = useContext(UserContext);
  const { getEORedirectUrl } = useEORedirectUrl();
  const { openIntercom, isIntercomAvailable } = useOpenIntercom();
  const { shouldRedirect: shouldRedirectToEOSite, eoRedirectUrl } =
    useEORedirect(!isLoggingInitialised);
  const {
    openModal: openRootModal,
    disableClose: disableRootClose,
    enableClose: enableRootClose,
    closeModal: closeRootModal,
  } = useContext(RootModalsContext);
  const { openModal: openAuthModal, closeModal: closeAuthModal } = useContext(AuthModalsContext);

  const redirectTo = router.query.redirectTo as string;
  const status = router.query.status as string;
  const resetSuccess = router.query.resetSuccess as string;
  const accessToken = Cookies.get('accessToken');

  const form = useForm<SignInFormValues>({
    initialValues: {
      emailOrUsername: '',
      password: '',
    },
    validate: zodResolver(signInSchema),
    validateInputOnChange: true,
  });

  const completeMfaAuthentication = useCallback(
    async (authData: AuthSuccessResponse) => {
      setTokens(authData);

      try {
        const user = await me();
        const userData = user?.data;

        if (!userData) {
          throw new Error('Failed to load user profile after login');
        }

        reportEvent('User > Login', {
          userId: authData.id,
          brand: destination,
          platform,
          ...userData,
        });

        if (!destination) {
          window.sessionStorage.setItem('user', JSON.stringify(userData));
          setUser(userData);

          try {
            const kycResponse = await getKycVerify();
            if (kycResponse.data.status !== SocureKycStatus.ACCEPT) {
              setRedirect(getSafeRedirectPath(redirectTo));
              router.replace(Routes.userVerification());
              return;
            }
          } catch (error) {
            logError('Failed to check KYC status during MFA login', error);
          }
        }

        // Enable closing of the OTP modal
        enableRootClose();
        closeRootModal();

        const singleUseToken = authData.singleUseToken?.token ?? null;
        const redirectUrl = getEORedirectUrl(destination, platform, singleUseToken);
        handleRedirectAfterAuth(redirectUrl, true);
      } catch (error) {
        removeTokens();
        logError('Failed to complete MFA authentication', error);
        showNotification({
          title: t('errors.generic.title'),
          message: t('errors.generic.copy'),
          color: 'red',
          autoClose: 5000,
        });
      }
    },
    [
      destination,
      platform,
      setUser,
      setRedirect,
      getEORedirectUrl,
      handleRedirectAfterAuth,
      enableRootClose,
      closeRootModal,
      t,
      router,
      redirectTo,
    ]
  );

  const openTooManyAttemptsModal = useCallback(() => {
    openAuthModal({
      type: 'TooManyAttemptsModal',
      onContactSupport: () => {
        openIntercom("I'm having trouble verifying my email during login.");
      },
      onCancel: closeAuthModal,
    });
  }, [openAuthModal, closeAuthModal, openIntercom]);

  const handleLoginReset = useCallback(() => {
    enableRootClose();
    closeRootModal();
  }, [closeRootModal, enableRootClose]);

  const { handleLogin, isLoggingIn } = useLogin({
    onSuccess: (_, redirectUrl) => {
      handleRedirectAfterAuth(redirectUrl, true);
    },
    onMultiFactorRequired: (verificationToken, email, canResendInValue) => {
      disableRootClose();

      openRootModal({
        type: 'EmailVerificationModal',
        verificationToken,
        email,
        canResendIn: canResendInValue,
        onVerifySuccess: completeMfaAuthentication,
        onTooManyAttempts: openTooManyAttemptsModal,
        onStartOver: handleLoginReset,
        onContactSupport: () => {
          if (isIntercomAvailable) {
            openIntercom("I'm having trouble verifying my email during login.");
          } else {
            mailToSupport({
              subject: 'Email verification issue',
              body: "Hello, Splash Support - I'm having trouble verifying my email during login.",
            });
          }
        },
      });
    },
  });

  // User Already logged in. Select appropriate redirect URL
  useEffect(() => {
    // Prevent redirects after login has been initialised (it has it's own redirect handler)
    if (isLoggingInitialised) return;

    // Using access token specifically, because EO login doesn't set the user in the context or local storage
    if (accessToken) {
      // We need two separate checks, because we might have to wait for the redirectURL (waiting on SUT)
      if (shouldRedirectToEOSite && eoRedirectUrl) handleRedirectAfterAuth(eoRedirectUrl, true);
      if (!shouldRedirectToEOSite) handleRedirectAfterAuth(redirectTo);
    }
  }, [redirectTo, shouldRedirectToEOSite, eoRedirectUrl, accessToken, isLoggingInitialised]);

  const showErrorRYPNotification = useCallback(() => {
    const NOTIFICATION_CONFIGS = {
      [BRANDS.RYP]: {
        authSSODeprecated: {
          title: t('ssoLoginDisabled.title'),
          message: t('ssoLoginDisabled.copy'),
        },
        authError: {
          title: t('errors.authError.title'),
          message: t('errors.authError.copy'),
        },
        invalidSSO: {
          title: t('errors.generic.title'),
          message: t('errors.generic.copy'),
        },
        accountNotFound: {
          title: t('errors.ssoAccountNotFound.title'),
          message: t('errors.ssoAccountNotFound.copy'),
        },
      },
      [BRANDS.OFP]: {
        authError: {
          title: t('errors.generic.title'),
          message: t('errors.generic.copy'),
        },
      },
    };

    const config = NOTIFICATION_CONFIGS[brand]?.[status as keyof typeof NOTIFICATION_CONFIGS];
    if (config) {
      showNotification({
        title: config.title,
        message: config.message,
        color: 'red',
        autoClose: 5000,
      });
    }
  }, [brand, status, t]);

  useEffect(showErrorRYPNotification, [showErrorRYPNotification]);

  if (accessToken) {
    return (
      <MantineProvider theme={lightTheme}>
        <SplashLoader />
      </MantineProvider>
    );
  }

  return (
    <MantineProvider theme={lightTheme}>
      <div className={classes.signInContainer}>
        <SignInActions
          signInProps={{
            handleFormSubmit: form.onSubmit(({ emailOrUsername, password }) => {
              setIsLoggingInitialised(true);
              handleLogin({ email: emailOrUsername, password });
            }),
            emailFieldProps: form.getInputProps('emailOrUsername'),
            passwordFieldProps: form.getInputProps('password'),
            isFormLoading: isLoggingIn,
          }}
          resetSuccess={resetSuccess}
          brand={brand}
          platform={platform}
        />
      </div>
    </MantineProvider>
  );
}

function SignIn() {
  return (
    <AuthModalsContextProvider>
      <SignInContent />
    </AuthModalsContextProvider>
  );
}

export default SignIn;
